import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import type { ApiClient } from '../client';
import { DASHBOARD_ROLES, type DashboardRole } from '@/types/session';
import {
  DASHBOARD_ORGANISATION_TYPES,
  DASHBOARD_USER_STATUSES,
  type DashboardOrganisationType,
  type DashboardUserRow,
  type DashboardUserStatus,
} from '@/types/dashboardUser';

const dashboardUserRowSchema = z.object({
  id: z.string(),
  email: z.string(),
  displayName: z.string(),
  userType: z.enum(DASHBOARD_ROLES),
  status: z.enum(DASHBOARD_USER_STATUSES),
  organisationType: z.enum(DASHBOARD_ORGANISATION_TYPES).nullable(),
  organisationKey: z.string().nullable(),
  dateAdded: z.string(),
}) satisfies z.ZodType<DashboardUserRow>;

export interface UsersPage {
  items: DashboardUserRow[];
  skip: number;
  take: number;
  total: number;
}

const usersPageSchema = z.object({
  items: z.array(dashboardUserRowSchema),
  skip: z.number(),
  take: z.number(),
  total: z.number(),
}) satisfies z.ZodType<UsersPage>;

export interface UsersListParams {
  skip?: number | undefined;
  take?: number | undefined;
  search?: string | undefined;
  userType?: DashboardRole[] | undefined;
  status?: DashboardUserStatus[] | undefined;
}

/**
 * Prefix shared by every cached users list page, whatever its
 * skip/take/search/filter params. Match on this to invalidate or patch all of
 * them at once instead of guessing a single fully-specified key.
 */
export const usersListQueryKey = ['users', 'list'] as const;

export function usersQueryKey(params: UsersListParams) {
  return [...usersListQueryKey, params] as const;
}

function buildUsersQuery(params: UsersListParams): string {
  const query = new URLSearchParams();
  if (params.skip !== undefined) query.set('skip', String(params.skip));
  if (params.take !== undefined) query.set('take', String(params.take));
  if (params.search) query.set('search', params.search);
  if (params.userType?.length) query.set('userType', params.userType.join(','));
  if (params.status?.length) query.set('status', params.status.join(','));
  const qs = query.toString();
  return qs ? `?${qs}` : '';
}

/** GET /api/v1/users. Tollit-only — the server enforces `UserManagement:Read`. */
export function usersQueryOptions(api: ApiClient, params: UsersListParams = {}) {
  return queryOptions({
    queryKey: usersQueryKey(params),
    queryFn: async () =>
      usersPageSchema.parse(await api.request<unknown>(`/api/v1/users${buildUsersQuery(params)}`)),
    staleTime: 30 * 1000,
  });
}

export function userQueryKey(id: string) {
  return ['users', 'detail', id] as const;
}

/** GET /api/v1/users/{id}. Tollit-only — the server enforces `UserManagement:Read`. */
export function userQueryOptions(api: ApiClient, id: string) {
  return queryOptions({
    queryKey: userQueryKey(id),
    queryFn: async () =>
      dashboardUserRowSchema.parse(await api.request<unknown>(`/api/v1/users/${id}`)),
    staleTime: 30 * 1000,
  });
}

export interface InviteUserRequest {
  email: string;
  displayName: string;
  userType: DashboardRole;
  organisationType?: DashboardOrganisationType;
  organisationKey?: string;
}

/** POST /api/v1/users/invite. Tollit-only — the server enforces `UserManagement:Write`. */
export function inviteUserMutationOptions(api: ApiClient) {
  return {
    mutationFn: async (body: InviteUserRequest) =>
      dashboardUserRowSchema.parse(
        await api.request<unknown>('/api/v1/users/invite', {
          method: 'POST',
          body: JSON.stringify(body),
        }),
      ),
  };
}

export interface UpdateUserRequest {
  displayName: string;
  userType: DashboardRole;
  organisationType?: DashboardOrganisationType;
  organisationKey?: string;
}

/**
 * PUT /api/v1/users/{id}. Tollit-only — the server enforces
 * `UserManagement:Write`.
 *
 * PUT, not PATCH: the body is the user's whole new editable state. Role and
 * organisation are one coupled decision — which organisations are valid
 * depends on the userType being submitted — and with omittable fields "move
 * this user to no organisation" would be indistinguishable from "leave the
 * organisation alone". So organisationType and organisationKey travel together
 * or not at all.
 *
 * email and status are not accepted here: email is the binding key between the
 * row and the Firebase account, and status moves only through the
 * suspend/reactivate endpoints. The server also rejects an admin editing their
 * own account, which is what stops one demoting themselves out of user
 * management.
 */
export function updateUserMutationOptions(api: ApiClient) {
  return {
    mutationFn: async ({ id, body }: { id: string; body: UpdateUserRequest }) =>
      dashboardUserRowSchema.parse(
        await api.request<unknown>(`/api/v1/users/${encodeURIComponent(id)}`, {
          method: 'PUT',
          body: JSON.stringify(body),
        }),
      ),
  };
}

/**
 * POST /api/v1/users/{id}/suspend. Tollit-only. Only an Active user can be
 * suspended, and the server rejects an admin suspending their own account.
 */
export function suspendUserMutationOptions(api: ApiClient) {
  return {
    mutationFn: async (id: string) =>
      dashboardUserRowSchema.parse(
        await api.request<unknown>(`/api/v1/users/${encodeURIComponent(id)}/suspend`, {
          method: 'POST',
        }),
      ),
  };
}

/**
 * POST /api/v1/users/{id}/reactivate. Tollit-only. The inverse of suspend:
 * only a Deactivated user can be restored, and the server rejects an admin
 * targeting their own account. No email is sent — the user signs back in with
 * the password they already had.
 */
export function reactivateUserMutationOptions(api: ApiClient) {
  return {
    mutationFn: async (id: string) =>
      dashboardUserRowSchema.parse(
        await api.request<unknown>(`/api/v1/users/${encodeURIComponent(id)}/reactivate`, {
          method: 'POST',
        }),
      ),
  };
}

/**
 * POST /api/v1/users/{id}/resend-invite. Tollit-only. Mails a fresh
 * password-reset link to a user who is still Invited. The row comes back
 * unchanged — Invited has no transition to itself — so there is nothing to
 * patch into the cache; the only outcome is whether the send succeeded. The
 * server rejects a target that isn't Invited, and an admin targeting
 * themselves.
 */
export function resendInviteMutationOptions(api: ApiClient) {
  return {
    mutationFn: async (id: string) =>
      dashboardUserRowSchema.parse(
        await api.request<unknown>(`/api/v1/users/${encodeURIComponent(id)}/resend-invite`, {
          method: 'POST',
        }),
      ),
  };
}

/**
 * POST /api/v1/users/{id}/second-factors/reset. Tollit-only — the server
 * enforces the same UserManagement feature as suspend and delete. Removes the
 * user's enrolled second factors and revokes their sessions; they enroll again
 * at their next sign-in. 204, so there is no row to patch into the cache.
 *
 * An account-takeover path if an admin account is compromised (backend
 * decision D4), which is why the screen puts it behind a confirmation.
 */
export function resetSecondFactorsMutationOptions(api: ApiClient) {
  return {
    mutationFn: async (id: string) => {
      await api.request<void>(`/api/v1/users/${encodeURIComponent(id)}/second-factors/reset`, {
        method: 'POST',
      });
      return id;
    },
  };
}

/**
 * DELETE /api/v1/users/{id}. Tollit-only. Soft-deletes the dashboard user and
 * permanently deletes their Firebase Auth account — irreversible. The server
 * rejects an admin deleting their own account.
 */
export function deleteUserMutationOptions(api: ApiClient) {
  return {
    mutationFn: async (id: string) => {
      await api.request<void>(`/api/v1/users/${encodeURIComponent(id)}`, { method: 'DELETE' });
      return id;
    },
  };
}

export interface OrganisationOption {
  key: string;
  name: string;
}

export interface UserOrganisationOptions {
  organisationType: DashboardOrganisationType | null;
  items: OrganisationOption[];
}

const organisationOptionSchema = z.object({
  key: z.string().nullable(),
  name: z.string().nullable(),
});

const userOrganisationOptionsSchema = z
  .object({
    organisationType: z.enum(DASHBOARD_ORGANISATION_TYPES).nullable(),
    items: z.array(organisationOptionSchema).nullable(),
  })
  .transform((value): UserOrganisationOptions => ({
    organisationType: value.organisationType,
    // Drop any entry missing a key or name — nothing useful to submit or show.
    items: (value.items ?? []).flatMap((item) =>
      item.key && item.name ? [{ key: item.key, name: item.name }] : [],
    ),
  }));

export function userOrganisationsQueryKey(userType: DashboardRole) {
  return ['users', 'organisations', userType] as const;
}

/**
 * GET /api/v1/users/organisations?userType=. Backs the invite screen's
 * Organisation dropdown. The server is the source of truth for which roles
 * need an organisation at all: it returns organisationType: null and an empty
 * items list for a role that takes none, rather than the caller guessing from
 * a hardcoded role map.
 */
export function userOrganisationsQueryOptions(api: ApiClient, userType: DashboardRole) {
  return queryOptions({
    queryKey: userOrganisationsQueryKey(userType),
    queryFn: async () =>
      userOrganisationOptionsSchema.parse(
        await api.request<unknown>(
          `/api/v1/users/organisations?userType=${encodeURIComponent(userType)}`,
        ),
      ),
    staleTime: 5 * 60 * 1000,
  });
}
